/* eslint-disable import/prefer-default-export */
import * as React from "react";
import { ReviewStars, VisuallyHidden } from "@components";
import classNames from "classnames";
import { useRouter } from "next/router";

import { LocaleCode } from "../../lib/util/locale";

// TODO: make these values locale aware
const starRatingAccessibleText = "out of 5 stars";
const reviewsAccessibleText = "reviews";

export function formatReviewsCount(count: number, locale: LocaleCode) {
  try {
    const formatter = new Intl.NumberFormat(locale, {
      notation: "compact",
      compactDisplay: "short",
    });
    return formatter.format(count);
  } catch {
    return count;
  }
}

interface ReviewCountProps {
  reviewCount: number;
  stars: number;
  mobileStack?: boolean;
  condensed?: boolean;
}

/**
 * Displays the average rating and review count of a product
 */
export function ReviewCount({
  reviewCount,
  stars,
  mobileStack,
  condensed,
}: ReviewCountProps) {
  const { locale } = useRouter();
  const shouldRender = reviewCount > 10 && stars > 3;
  const formattedReviewCount = formatReviewsCount(
    reviewCount,
    locale as LocaleCode
  );

  return shouldRender ? (
    <p
      className={classNames("flex uppercase text-slate", {
        "flex-col pb-2 md:flex-row md:pb-0 text-center items-center":
          !!mobileStack,
        "items-center": !mobileStack,
      })}
      data-testid="product-rating"
    >
      <span
        className={classNames("inline-block text-pine leading-none", {
          "border-none pb-2 pr-0 md:border-r md:border-solid md:pb-0":
            !!mobileStack,
          "border-x px-3 order-2 py-[1px]": !mobileStack,
          "border-none px-0": condensed,
        })}
      >
        <ReviewStars stars={stars} />
      </span>
      <span
        className={classNames("inline-block text-base truncate leading-none", {
          "border-none pb-2 md:border-r md:border-solid md:pb-0": !!mobileStack,
          "pr-3 order-1": !mobileStack,
          "border-none pb-0 px-0 text-[11px] md:px-0 xl:px-5": condensed,
        })}
      >
        {`${stars}/5`}
        {mobileStack && (
          <span className="visible md:hidden">
            &nbsp;{starRatingAccessibleText}
          </span>
        )}
        <VisuallyHidden>{starRatingAccessibleText}</VisuallyHidden>
      </span>
      {!condensed && (
        <span className="order-3 inline-block px-3 text-sm leading-none hover:underline">
          {`${formattedReviewCount} ${reviewsAccessibleText}`}
        </span>
      )}
    </p>
  ) : null;
}
